import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";

const FOCUSABLE =
  "a[href],button:not([disabled]),input:not([disabled]):not([type='hidden']),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex='-1'])";

export interface BottomSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  /** Visually hide the title (still the dialog's accessible name). */
  readonly hideTitle?: boolean;
  readonly children?: ReactNode;
  readonly testId?: string;
  readonly className?: string;
}

const CLOSE_DRAG_PX = 80;

/**
 * Bottom sheet: portal, `role=dialog aria-modal`, drag handle (swipe down closes), max 85dvh,
 * safe-area padding, focus trap, Esc / overlay close, focus returns to the opener.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  hideTitle = true,
  children,
  testId,
  className,
}: BottomSheetProps) {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dragFrom = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const body = panel?.querySelector<HTMLElement>("[data-sheet-body]");
    (body?.querySelector<HTMLElement>(FOCUSABLE) ?? panel)?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== "Tab" || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      e.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid={testId}>
      <div
        aria-hidden="true"
        data-testid="sheet-overlay"
        className="absolute inset-0 bg-neutral-950/50"
        onClick={() => onCloseRef.current()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cx(
          "relative flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-2xl bg-surface text-fg shadow-lg focus:outline-none",
          "pb-[env(safe-area-inset-bottom)]",
          className,
        )}
      >
        <div
          data-testid="sheet-handle"
          aria-hidden="true"
          className="flex shrink-0 touch-none justify-center py-3"
          onPointerDown={(e) => {
            dragFrom.current = e.clientY;
          }}
          onPointerUp={(e) => {
            if (dragFrom.current !== null && e.clientY - dragFrom.current > CLOSE_DRAG_PX)
              onCloseRef.current();
            dragFrom.current = null;
          }}
          onPointerCancel={() => {
            dragFrom.current = null;
          }}
        >
          <span className="h-1 w-10 rounded-full bg-line-strong" />
        </div>
        <h2
          id={titleId}
          className={cx("px-4 pb-2 text-base font-semibold", hideTitle && "sr-only")}
        >
          {title}
        </h2>
        <button type="button" className="sr-only" onClick={() => onCloseRef.current()}>
          {t("ui.modal.close")}
        </button>
        <div data-sheet-body="" className="min-h-0 overflow-y-auto px-4 pb-4">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
